import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  DollarSign, 
  TrendingUp, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  X, 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  ArrowUpRight,
  PieChart,
  Layers
} from 'lucide-react';
import { ProjectRevenueRecord, InvoiceStatus, FinancialSummary } from '@ftt/shared';

export const AdminRevenuePage: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedService, setSelectedService] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState<ProjectRevenueRecord | null>(null);

  // Form State
  const [projectTitle, setProjectTitle] = useState('');
  const [clientOrPartnerName, setClientOrPartnerName] = useState('');
  const [serviceType, setServiceType] = useState<any>('BOOK_PUBLISHING');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');
  const [contractValue, setContractValue] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [productionCost, setProductionCost] = useState(0);
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatus>('ISSUED');
  const [dueDate, setDueDate] = useState('');
  const [paymentDate, setPaymentDate] = useState('');

  const queryClient = useQueryClient();

  const { data: revenuesRes, isLoading } = useQuery({
    queryKey: ['admin-revenues', { status: selectedStatus, service: selectedService, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/revenues';
      const params = new URLSearchParams();
      if (selectedStatus !== 'ALL') params.append('status', selectedStatus);
      if (selectedService !== 'ALL') params.append('serviceType', selectedService);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const { data: summaryRes } = useQuery({
    queryKey: ['admin-financials-summary'],
    queryFn: () => api.get('/admin/financials/summary').then((res) => res.data.data),
  });

  const revenues: ProjectRevenueRecord[] = revenuesRes || [];
  const summary: FinancialSummary = summaryRes || {
    totalContractedRevenueINR: 0,
    totalReceivedRevenueINR: 0,
    totalOutstandingINR: 0,
    totalProductionCostINR: 0,
    netProfitINR: 0,
    publishingProjectsRevenueINR: 0,
    translationProjectsRevenueINR: 0,
    activeProjectsCount: 0,
    activeTranslationsCount: 0,
    activePartnersCount: 0,
    activeCustomersCount: 0,
    monthlyRevenueTrend: [],
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        projectTitle,
        clientOrPartnerName,
        serviceType,
        currency,
        contractValue,
        receivedAmount,
        productionCost,
        invoiceStatus,
        dueDate: dueDate || null,
        paymentDate: paymentDate || null,
      };

      if (editingRevenue?.id) {
        return api.put(`/admin/revenues/${editingRevenue.id}`, payload);
      } else {
        return api.post('/admin/revenues', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-revenues'] });
      queryClient.invalidateQueries({ queryKey: ['admin-financials-summary'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/revenues/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-revenues'] });
      queryClient.invalidateQueries({ queryKey: ['admin-financials-summary'] });
    },
  });

  const resetForm = () => {
    setEditingRevenue(null);
    setProjectTitle('');
    setClientOrPartnerName('');
    setServiceType('BOOK_PUBLISHING');
    setCurrency('INR');
    setContractValue(0);
    setReceivedAmount(0);
    setProductionCost(0);
    setInvoiceStatus('ISSUED');
    setDueDate('');
    setPaymentDate('');
  };

  const handleOpenEdit = (record: ProjectRevenueRecord) => {
    setEditingRevenue(record);
    setProjectTitle(record.projectTitle);
    setClientOrPartnerName(record.clientOrPartnerName);
    setServiceType(record.serviceType);
    setCurrency(record.currency);
    setContractValue(record.contractValue);
    setReceivedAmount(record.receivedAmount);
    setProductionCost(record.productionCost);
    setInvoiceStatus(record.invoiceStatus);
    setDueDate(record.dueDate ? record.dueDate.split('T')[0] : '');
    setPaymentDate(record.paymentDate ? record.paymentDate.split('T')[0] : '');
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              FINANCIAL MANAGEMENT
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <DollarSign className="w-7 h-7 text-[#FF6B4A]" />
            <span>Revenue, Invoicing &amp; Services</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Real-time tracking of contracted revenue, translation milestones, production expenses, and gross margins.
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Generate Project Invoice</span>
        </button>
      </div>

      {/* 2. 4 Core Financial KPI Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-semibold">Total Contract Value</span>
            <div className="w-7 h-7 rounded-lg bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-mono">
            ₹{summary.totalContractedRevenueINR.toLocaleString('en-IN')}
          </div>
          <span className="text-[10px] text-slate-500 block font-sans">Across all active contracts</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 font-semibold">Realized Revenue</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-emerald-700 font-mono">
            ₹{summary.totalReceivedRevenueINR.toLocaleString('en-IN')}
          </div>
          <span className="text-[10px] text-emerald-600 block font-sans font-medium">
            {summary.totalContractedRevenueINR > 0 ? Math.round((summary.totalReceivedRevenueINR / summary.totalContractedRevenueINR) * 100) : 0}% collected
          </span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700 font-semibold">Pending Receivables</span>
            <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-amber-700 font-mono">
            ₹{summary.totalOutstandingINR.toLocaleString('en-IN')}
          </div>
          <span className="text-[10px] text-slate-500 block font-sans">Milestone balance due</span>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-purple-700 font-semibold">Net Operating Margin</span>
            <div className="w-7 h-7 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-700">
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-purple-700 font-mono">
            ₹{summary.netProfitINR.toLocaleString('en-IN')}
          </div>
          <span className="text-[10px] text-slate-500 block font-sans">
            Cost: ₹{summary.totalProductionCostINR.toLocaleString('en-IN')}
          </span>
        </div>
      </div>

      {/* 3. Monthly Trend & Service Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Monthly Trend Visual */}
        <div className="lg:col-span-8 p-6 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-4 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-serif text-base font-bold text-slate-900">Monthly Cash Flow &amp; Expense Velocity</h3>
            <span className="text-[10px] font-mono text-slate-500">Last 6 Months (INR ₹)</span>
          </div>

          <div className="space-y-3 pt-2">
            {summary.monthlyRevenueTrend?.map((item) => (
              <div key={item.month} className="space-y-1">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-slate-800 font-medium">{item.month}</span>
                  <div className="space-x-3 text-[11px]">
                    <span className="text-emerald-700 font-bold">+₹{item.revenueINR.toLocaleString('en-IN')}</span>
                    <span className="text-rose-600 font-medium">-₹{item.expenseINR.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 flex overflow-hidden border border-slate-200/60">
                  <div 
                    className="bg-emerald-500 h-2" 
                    style={{ width: `${Math.min(100, (item.revenueINR / 350000) * 100)}%` }}
                  />
                  <div 
                    className="bg-rose-400 h-2 opacity-75" 
                    style={{ width: `${Math.min(100, (item.expenseINR / 350000) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Service Category Split */}
        <div className="lg:col-span-4 p-6 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-4 flex flex-col justify-between shadow-sm">
          <div className="border-b border-slate-100 pb-3">
            <h3 className="font-serif text-base font-bold text-slate-900">Services Revenue Split</h3>
            <span className="text-[10px] font-mono text-slate-500">Distribution by Publishing Stream</span>
          </div>

          <div className="space-y-4 py-2">
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-brand-700 font-semibold">Original Publishing</span>
                <span className="text-slate-900 font-bold">₹{summary.publishingProjectsRevenueINR.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-slate-600">Monographs, commentaries, and devotional works</p>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-purple-700 font-semibold">Translation &amp; Vernacular</span>
                <span className="text-slate-900 font-bold">₹{summary.translationProjectsRevenueINR.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-slate-600">Hindi, Tamil, and Telugu translation pipelines</p>
            </div>
          </div>

          <div className="pt-2 text-[10px] text-slate-500 font-mono border-t border-slate-100">
            Corporate Entity: FOR THE TRUTH PRESS PRIVATE LIMITED
          </div>
        </div>
      </div>

      {/* 4. Invoices and Project Revenue Table */}
      <div className="space-y-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search invoice or project..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:border-brand-500 w-56 sm:w-64 transition-colors"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600 font-medium">Status:</span>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 focus:outline-hidden focus:border-brand-500"
              >
                <option value="ALL">All Statuses</option>
                <option value="PAID">Paid</option>
                <option value="PARTIALLY_PAID">Partially Paid</option>
                <option value="ISSUED">Issued / Pending</option>
                <option value="OVERDUE">Overdue</option>
              </select>
            </div>
          </div>

          <div className="text-xs text-slate-500 font-mono">
            Showing <strong>{revenues.length}</strong> invoices
          </div>
        </div>

        <div className="rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead className="bg-slate-50/80 text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-3.5 px-4 font-bold">Invoice #</th>
                  <th className="py-3.5 px-4 font-bold">Project Title &amp; Client</th>
                  <th className="py-3.5 px-4 font-bold">Service Type</th>
                  <th className="py-3.5 px-4 font-bold">Contract Value</th>
                  <th className="py-3.5 px-4 font-bold">Paid / Pending</th>
                  <th className="py-3.5 px-4 font-bold">Margin</th>
                  <th className="py-3.5 px-4 font-bold">Status</th>
                  <th className="py-3.5 px-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-xs font-mono text-slate-500">
                      Loading invoice records...
                    </td>
                  </tr>
                ) : revenues.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-xs text-slate-500">
                      No invoices found matching criteria.
                    </td>
                  </tr>
                ) : (
                  revenues.map((rev) => (
                    <tr key={rev.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-4 px-4 font-mono font-bold text-brand-700">
                        {rev.invoiceNumber}
                      </td>

                      <td className="py-4 px-4">
                        <span className="font-serif font-bold text-sm text-slate-900 block">{rev.projectTitle}</span>
                        <span className="text-[11px] text-slate-500">{rev.clientOrPartnerName}</span>
                      </td>

                      <td className="py-4 px-4">
                        <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 font-semibold">
                          {rev.serviceType.replace(/_/g, ' ')}
                        </span>
                      </td>

                      <td className="py-4 px-4 font-mono font-bold text-slate-900">
                        ₹{rev.contractValue.toLocaleString('en-IN')}
                      </td>

                      <td className="py-4 px-4 font-mono">
                        <div className="text-emerald-700 font-semibold">₹{rev.receivedAmount.toLocaleString('en-IN')}</div>
                        {rev.pendingAmount > 0 && (
                          <div className="text-amber-700 text-[10px] font-bold">₹{rev.pendingAmount.toLocaleString('en-IN')} due</div>
                        )}
                      </td>

                      <td className="py-4 px-4 font-mono text-purple-700 font-semibold">
                        ₹{rev.netMargin.toLocaleString('en-IN')}
                      </td>

                      <td className="py-4 px-4">
                        <span className={`text-[10px] font-mono px-2.5 py-1 rounded-full font-bold ${
                          rev.invoiceStatus === 'PAID'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : rev.invoiceStatus === 'PARTIALLY_PAID'
                            ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {rev.invoiceStatus.replace(/_/g, ' ')}
                        </span>
                      </td>

                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEdit(rev)}
                            className="p-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                            title="Edit Invoice"
                          >
                            <Edit3 className="w-3.5 h-3.5 text-[#FF6B4A]" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Remove invoice "${rev.invoiceNumber}"?`)) {
                                deleteMutation.mutate(rev.id);
                              }
                            }}
                            className="p-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 transition-colors border border-rose-200 cursor-pointer"
                            title="Delete Invoice"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 5. Add / Edit Invoice Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-brand-700 font-bold">INVOICE CREATION</span>
                <h2 className="text-xl font-serif font-bold text-slate-900">
                  {editingRevenue ? 'Edit Project Invoice' : 'Create New Project Invoice'}
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
              className="space-y-4 text-xs font-sans"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Project Title *</label>
                  <input
                    type="text"
                    required
                    value={projectTitle}
                    onChange={(e) => setProjectTitle(e.target.value)}
                    placeholder="e.g. The Message of Acts (Hindi Edition)"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Client / Partner Name *</label>
                  <input
                    type="text"
                    required
                    value={clientOrPartnerName}
                    onChange={(e) => setClientOrPartnerName(e.target.value)}
                    placeholder="e.g. InterVarsity Press / Seminary Co-op"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Service Type</label>
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="BOOK_PUBLISHING">Book Publishing</option>
                    <option value="TRANSLATION_SERVICES">Translation Services</option>
                    <option value="EDITORIAL_ASSESSMENT">Editorial Assessment</option>
                    <option value="CUSTOM_TYPESETTING">Custom Typesetting</option>
                    <option value="RIGHTS_LICENSING">Rights Licensing</option>
                    <option value="BULK_PRINTING">Bulk Printing</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Invoice Status</label>
                  <select
                    value={invoiceStatus}
                    onChange={(e) => setInvoiceStatus(e.target.value as InvoiceStatus)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  >
                    <option value="PAID">Paid</option>
                    <option value="PARTIALLY_PAID">Partially Paid</option>
                    <option value="ISSUED">Issued / Pending</option>
                    <option value="DRAFT">Draft</option>
                    <option value="OVERDUE">Overdue</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Contract Value *</label>
                  <input
                    type="number"
                    required
                    value={contractValue}
                    onChange={(e) => setContractValue(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Received Amount</label>
                  <input
                    type="number"
                    value={receivedAmount}
                    onChange={(e) => setReceivedAmount(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Production Cost</label>
                  <input
                    type="number"
                    value={productionCost}
                    onChange={(e) => setProductionCost(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Invoice Due Date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Payment Date (if paid)</label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-md transition-colors cursor-pointer disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving Invoice...' : editingRevenue ? 'Update Invoice' : 'Save Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
